import { create } from "zustand";
import { channelStore, useChannelRoomStore } from ".";
import { UserInfo } from "@obsbot/remo-server-api";

export interface User {
    userUUID: string;
    rtcUID: string;
    avatar: string;
    name: string;
    camera: boolean;
    mic: boolean;
    isSpeak: boolean;
    isListen: boolean;
    hasLeft: boolean;
    networkQuality: {
        delay: number; // 网络延迟(ms)
        uplink: number; // 上行质量(0-5)
        downlink: number; // 下行质量(0-5)
    };
}

export interface InitUserStoreConfig {
    roomUUID: string;
    userUUID: string;
}

let isInit = false;

export interface UserStore {
    roomUUID: string;
    userUUID: string;
    cachedUsers: Map<string, User>;
    creator: User | null;
    currentUser: User | null;
    speakingJoiners: User[];
    otherJoiners: User[];
    joiners: User[];
    isInRoom: (userUUID: string) => boolean;
    initCurrentUser: (userUUID: string) => Promise<void>;
    initUserStore: (config: InitUserStoreConfig) => UserStore;
    initUsers: (userUUIDs: string[], forceUserUUIDs?: string[]) => Promise<void>;
    createUsers: (userUUIDs: string[]) => Promise<User[]>;
    createLazyUsers: (userUUIDs: string[], force?: boolean) => User[];
    updateNetworkQuality: (userUUID: string, quality: User["networkQuality"]) => void;
    addUser: (userUUID: string, force?: boolean) => Promise<User>;
    cacheUser: (user: User) => void;
    cacheUsers: (users: User[]) => void;
    cacheUserIfNeeded: (userUUID: string, userInfo: UserInfo) => boolean;
    removeUser: (userUUID: string) => void;
    updateUsers: (editUser: (user: User) => boolean | void) => void;
    syncExtraUsersInfo: (userUUIDs: string[]) => Promise<void>;
    flushLazyUsers: (wanted?: string[]) => Promise<void>;
    pickRandomJoiner: () => User | undefined;
    sortUser: (user: User) => void;
    updateCurrentUser: (partialUser: Partial<User>) => void;
}

export const useUserStore = create<UserStore>((set, get) => {
    // set的中间件，用于处理用户信息的更新同步到游戏覆盖窗口
    const setMiddleware: typeof set = partial => {
        if (typeof partial === "function") {
            return set(state => {
                const result = partial(state);
                return result as UserStore | Partial<UserStore>;
            }, false);
        } else if (partial && "cachedUsers" in partial && "currentUser" in partial) {
            // @ts-ignore
            if (partial.cachedUsers?.has(undefined)) {
                // @ts-ignore
                partial.cachedUsers.delete(undefined);
            }
            (window as any).electron?.ipcRenderer.send("resend-to-overlay-window", {
                eventName: "get-cached-user",
                data: partial.cachedUsers,
            });
        } else if (partial && "cachedUsers" in partial) {
            // @ts-ignore
            if (partial.cachedUsers?.has(undefined)) {
                // @ts-ignore
                partial.cachedUsers.delete(undefined);
            }
            (window as any).electron?.ipcRenderer.send("resend-to-overlay-window", {
                eventName: "get-cached-user",
                data: partial.cachedUsers,
            });
        }
        return set(partial as UserStore | Partial<UserStore>, false);
    };
    return {
        roomUUID: "",
        userUUID: "",
        ownerUUID: "",
        cachedUsers: new Map(),
        creator: null,
        currentUser: null,
        speakingJoiners: [],
        otherJoiners: [],
        isInRoom: () => false,
        initUserStore(config) {
            if (isInit) {
                return this;
            }
            setMiddleware({
                roomUUID: config.roomUUID,
                userUUID: config.userUUID,
            });
            isInit = true;
            return this;
        },
        initCurrentUser: async userUUID => {
            const currentUser = await get().createUsers([userUUID]);
            console.log("[WEB] [LOG] 加入房间调用initCurrentUser", currentUser[0]);
            setMiddleware({ currentUser: currentUser[0] });
        },
        initUsers: async (userUUIDs, forceUserUUIDs) => {
            const { createUsers, createLazyUsers, sortUser, cacheUser } = get();
            setMiddleware({
                otherJoiners: [],
                speakingJoiners: [],
            });

            const users = await createUsers(forceUserUUIDs || userUUIDs);
            users.forEach(user => {
                sortUser(user);
                cacheUser(user);
            });

            if (forceUserUUIDs) {
                const lazyUsers = userUUIDs.filter(userUUID => !forceUserUUIDs.includes(userUUID));
                createLazyUsers(lazyUsers).forEach(user => {
                    sortUser(user);
                    cacheUser(user);
                });
            }
        },

        addUser: async (userUUID, force = false) => {
            const { cachedUsers, removeUser, createUsers, createLazyUsers, sortUser, cacheUser } =
                get();

            if (cachedUsers.has(userUUID)) {
                removeUser(userUUID);
            }

            let user: User;
            if (force) {
                user = (await createUsers([userUUID]))[0];
            } else {
                user = createLazyUsers([userUUID], true)[0];
            }

            cacheUser(user);
            sortUser(user);
            return user;
        },

        cacheUserIfNeeded: (userUUID, userInfo) => {
            const { cachedUsers, createLazyUsers, sortUser, cacheUser } = get();

            let user = cachedUsers.get(userUUID);
            if (!user) {
                user = createLazyUsers([userUUID])[0];
                cacheUser(user);
                sortUser(user);
            }

            let updated = false;
            if (!user.rtcUID) {
                setMiddleware({
                    cachedUsers: new Map(
                        cachedUsers.set(userUUID, {
                            ...user,
                            name: userInfo.name,
                            avatar: userInfo.avatarURL,
                            rtcUID: String(userInfo.rtcUID),
                        }),
                    ),
                });
                updated = true;
            }
            return updated;
        },

        removeUser: userUUID => {
            const { creator, speakingJoiners, otherJoiners, cachedUsers } = get();

            if (creator && creator.userUUID === userUUID) {
                setMiddleware({ creator: { ...creator, hasLeft: true } });
            } else {
                if (cachedUsers.has(userUUID)) {
                    const newCachedUsers = new Map(cachedUsers);
                    newCachedUsers.delete(userUUID);
                    setMiddleware({ cachedUsers: newCachedUsers });
                }
                const groups = [
                    { group: "speakingJoiners", users: speakingJoiners },
                    { group: "otherJoiners", users: otherJoiners },
                ];

                for (const { group, users } of groups) {
                    const index = users.findIndex(u => u.userUUID === userUUID);
                    if (index >= 0) {
                        const updatedUsers = [...users];
                        updatedUsers[index].hasLeft = true;
                        updatedUsers.splice(index, 1);
                        setMiddleware({ [group]: updatedUsers });
                        break;
                    }
                }
            }
        },

        updateUsers: editUser => {
            const { creator, speakingJoiners, otherJoiners, sortUser } = get();
            const unSortedUsers: User[] = [];
            let shouldStopEditUser = false;

            if (creator) {
                shouldStopEditUser = editUser(creator) === false;
            }

            const groups = [
                {
                    group: "speakingJoiners",
                    users: speakingJoiners,
                    shouldMoveOut: (u: User) => !u.isSpeak,
                },
                { group: "otherJoiners", users: otherJoiners, shouldMoveOut: () => false },
            ];

            for (const { group, users, shouldMoveOut } of groups) {
                if (shouldStopEditUser) {
                    break;
                }

                const updatedUsers = [...users];
                for (let i = 0; i < updatedUsers.length; i++) {
                    if (shouldStopEditUser) {
                        break;
                    }

                    const user = updatedUsers[i];
                    shouldStopEditUser = editUser(user) === false;

                    if (shouldMoveOut(user)) {
                        updatedUsers.splice(i, 1);
                        i--;
                        unSortedUsers.push(user);
                    }
                }
                setMiddleware({ [group]: updatedUsers });
            }

            unSortedUsers.forEach(sortUser);
        },

        syncExtraUsersInfo: async userUUIDs => {
            const { cachedUsers, createUsers, cacheUsers } = get();
            const users = await createUsers(
                userUUIDs.filter(userUUID => !cachedUsers.has(userUUID)),
            );
            cacheUsers(users);
        },

        flushLazyUsers: async wanted => {
            const { cachedUsers, cacheUsers, createUsers, removeUser, sortUser } = get();
            const userUUIDs: string[] = [];

            if (wanted) {
                for (const userUUID of wanted) {
                    const user = cachedUsers.get(userUUID);
                    if (!user || !user.rtcUID) {
                        userUUIDs.push(userUUID);
                    }
                }
            } else {
                // @ts-ignore
                for (const user of cachedUsers.values()) {
                    if (!user.rtcUID) {
                        userUUIDs.push(user.userUUID);
                    }
                }
            }

            if (userUUIDs.length === 0) {
                return;
            }

            const users = await createUsers(userUUIDs);
            cacheUsers(users);
            for (const user of users) {
                removeUser(user.userUUID);
                sortUser(user);
            }
        },

        joiners: [
            ...(get() ?? { speakingJoiners: [] }).speakingJoiners,
            ...(get() ?? { otherJoiners: [] }).otherJoiners,
        ],

        pickRandomJoiner: () => {
            const { joiners, userUUID } = get();
            const startIndex = Math.floor(Math.random() * joiners.length);

            for (let count = 0; count < joiners.length; count++) {
                const joiner = joiners[(startIndex + count) % joiners.length];
                if (joiner && joiner.userUUID !== userUUID) {
                    return joiner;
                }
            }
            return undefined;
        },

        // Private methods (not exposed in the store)
        createUsers: async (userUUIDs: string[]): Promise<User[]> => {
            if (userUUIDs.length === 0) {
                return [];
            }
            const usersInfoResult =
                (await useChannelRoomStore
                    .getState()
                    // @ts-ignore 去重
                    .rtm?.getUserProfile([...new Set(userUUIDs)])) || [];

            return userUUIDs.map(userUUID => {
                const current = get().currentUser;
                const userProfile = usersInfoResult.find(item => item.userID === userUUID);
                const remote = get().cachedUsers.get(userUUID);
                if (userUUID === current?.userUUID) {
                    return {
                        userUUID,
                        rtcUID: String(userProfile?.userID || ""),
                        avatar: userProfile?.avatar || "",
                        name: userProfile?.nick || "",
                        camera: channelStore.userState.camera,
                        mic: channelStore.userState.mic,
                        isSpeak: false,
                        hasLeft: false,
                        isListen: channelStore.userState.isListen,
                        networkQuality: {
                            delay: 0,
                            uplink: 0,
                            downlink: 0,
                        },
                    };
                } else {
                    return {
                        userUUID,
                        rtcUID: String(userProfile?.userID || ""),
                        avatar: userProfile?.avatar || "",
                        name: userProfile?.nick || "",
                        camera: remote?.camera || false,
                        mic: remote?.mic || false,
                        isSpeak: false,
                        hasLeft: remote?.hasLeft || false,
                        isListen: remote?.isListen || false,
                        networkQuality: {
                            delay: 0,
                            uplink: 0,
                            downlink: 0,
                        },
                    };
                }
            });
        },

        createLazyUsers: (userUUIDs: string[], withRtcUID = false): User[] => {
            return userUUIDs.map(userUUID => ({
                userUUID,
                rtcUID: withRtcUID ? String(Math.floor(Math.random() * 100000)) : "",
                avatar: "",
                name: "",
                camera: false,
                mic: false,
                isSpeak: false,
                hasLeft: false,
                isListen: false,
                networkQuality: {
                    delay: 0,
                    uplink: 0,
                    downlink: 0,
                },
            }));
        },

        updateNetworkQuality: (
            userUUID: string,
            quality: { delay: number; uplink: number; downlink: number },
        ) => {
            const { cachedUsers, currentUser } = get();
            const user = cachedUsers.get(userUUID);
            if (user) {
                if (user.userUUID === currentUser?.userUUID) {
                    setMiddleware({
                        currentUser: {
                            ...currentUser,
                            networkQuality: quality,
                        },
                    });
                }
                setMiddleware({
                    cachedUsers: new Map(
                        cachedUsers.set(userUUID, {
                            ...user,
                            networkQuality: quality,
                        }),
                    ),
                });
            }
        },

        sortUser: (user: User) => {
            const { userUUID, speakingJoiners, otherJoiners } = get();

            if (user.userUUID === userUUID) {
                setMiddleware({ currentUser: user });
            }

            if (user.isSpeak) {
                const index = speakingJoiners.findIndex(
                    ({ userUUID }) => userUUID === user.userUUID,
                );
                const updatedSpeakingJoiners = [...speakingJoiners];
                if (index >= 0) {
                    updatedSpeakingJoiners.splice(index, 1);
                }
                updatedSpeakingJoiners.push(user);
                setMiddleware({ speakingJoiners: updatedSpeakingJoiners });
            } else {
                setMiddleware({ otherJoiners: [...otherJoiners, user] });
            }
        },

        cacheUser: (user: User) => {
            const { cachedUsers, currentUser } = get();
            if (user.userUUID === currentUser?.userUUID) {
                setMiddleware({ currentUser: user });
            }
            setMiddleware({ cachedUsers: new Map(cachedUsers.set(user.userUUID, user)) });
        },

        cacheUsers: (users: User[]) => {
            const { cachedUsers, currentUser } = get();
            const newMap = new Map(cachedUsers);
            const userUUIDSet = new Set(users.map(user => user.userUUID));
            // @ts-ignore
            for (const key of newMap.keys()) {
                if (!userUUIDSet.has(key)) {
                    newMap.delete(key);
                }
            }
            users.forEach(user => {
                if (user.userUUID === currentUser?.userUUID) {
                    setMiddleware({ currentUser: user });
                }
                newMap.set(user.userUUID, user);
            });
            setMiddleware({ cachedUsers: newMap });
        },

        updateCurrentUser: (partialUser: Partial<User>) => {
            const { currentUser, cachedUsers } = get();
            if (currentUser) {
                const updateUser = {
                    ...currentUser,
                    ...partialUser,
                };
                setMiddleware({
                    currentUser: updateUser,
                    cachedUsers: new Map(cachedUsers.set(currentUser.userUUID, updateUser)),
                });
            }
        },
    };
});

export const totalUserCount = (state: UserStore): number =>
    (state.creator && !state.creator.hasLeft ? 1 : 0) +
    (state.speakingJoiners.length + state.otherJoiners.length);
